import { HttpClient, HttpParams } from '@angular/common/http';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';

export interface FeldMappingDialogData {
    ld_id: number;
    titel?: string;
}

interface PdfFormField {
    name: string;
    type: string;
}

interface Platzhalter {
    key: string;
    label: string;
}

interface PageInfo {
    page: number;
    width: number;
    height: number;
}

export interface MappingEntry {
    kind: 'field' | 'overlay';
    name?: string;
    page?: number;
    x?: number;
    y?: number;
    size?: number;
    /** Textblock-Breite in PDF-Punkten. Leer = eine Zeile. */
    width?: number;
    /** Unterschriften-Höhe in PDF-Punkten. */
    height?: number;
    /** Anzahl Zeilen im Textblock. */
    lines?: number;
    placeholder: string;
}

export interface CustomFieldDef {
    key: string;
    label: string;
    /** text = Freitext; checkbox = X oder nix auf PDF */
    type?: 'text' | 'checkbox';
}

@Component({
    selector: 'kiene-lieferant-dokument-feld-mapping-dialog',
    templateUrl: './lieferant-dokument-feld-mapping-dialog.component.html',
    styleUrls: ['./lieferant-dokument-feld-mapping-dialog.component.scss'],
})
export class LieferantDokumentFeldMappingDialogComponent implements OnInit, OnDestroy {
    @ViewChild('pdfHost') pdfHost: ElementRef<HTMLElement>;

    loading = true;
    saving = false;
    pdfLoading = true;
    pdfError = '';
    /** ng2-pdf-viewer: Uint8Array funktioniert zuverlässig im Dialog (iframe/blob oft leer). */
    pdfSrc: Uint8Array | null = null;

    hasAcroform = false;
    detectedFields: PdfFormField[] = [];
    pages: PageInfo[] = [];
    pageCount = 1;
    currentPage = 1;
    platzhalter: Platzhalter[] = [];
    customFields: CustomFieldDef[] = [];
    newCustomLabel = '';
    newCustomType: 'text' | 'checkbox' = 'text';
    entries: MappingEntry[] = [];
    previewing = false;

    placeMode = false;
    placePlaceholder = '';
    /** Aufziehen einer Textzeile: Startpunkt und aktuelle Breite. */
    placeStart: { x: number; y: number } | null = null;
    placeDraft: { x: number; y: number; width: number; height: number } | null = null;
    private resizeWidthEntry: MappingEntry | null = null;

    /** Aktiver Drag eines Overlay-Markers (Anker = Textanfang links). */
    dragEntry: MappingEntry | null = null;
    dragging = false;

    /** Erzwingt Neuzeichnen der Marker nach Scroll/Resize der PDF-Seite. */
    layoutTick = 0;
    private scrollEl: HTMLElement | null = null;
    private readonly onViewerScroll = () => this.refreshMarkerLayout();
    private readonly onWindowResize = () => this.refreshMarkerLayout();

    constructor(
        public dialogRef: MatDialogRef<LieferantDokumentFeldMappingDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FeldMappingDialogData,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private http: HttpClient,
        private messageService: MessageService,
        private cdr: ChangeDetectorRef,
        private pdfViewer: PdfViewerDesktopService,
    ) {}

    ngOnInit(): void {
        const params = new HttpParams().set('ld_id', String(this.data.ld_id));
        this.loadPdf(params);
        this.api.get(this.apiBaseUrl + 'lieferant_dokument/read_form_fields.php', params).subscribe({
            next: (res: any) => {
                this.detectedFields = res.fields || [];
                this.hasAcroform = !!res.has_acroform;
                this.pages = res.pages || [];
                this.pageCount = Math.max(1, res.page_count || this.pages.length || 1);
                this.customFields = (res.custom_fields || []).map((c: CustomFieldDef) => ({
                    key: c.key,
                    label: c.label,
                    type: c.type === 'checkbox' ? 'checkbox' : 'text',
                }));
                const rawPh: Platzhalter[] = res.platzhalter || [];
                this.platzhalter = rawPh.filter(p => !(p.key || '').startsWith('custom.'));
                this.entries = (res.entries || []).map((e: MappingEntry) => ({ ...e }));
                if (!this.entries.length && this.detectedFields.length) {
                    this.entries = this.detectedFields.map(f => ({
                        kind: 'field' as const,
                        name: f.name,
                        placeholder: '',
                    }));
                }
                this.ensurePlacePlaceholder();
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.ensureFallbackPlatzhalter();
                this.messageService.errorMessage(
                    'Feldliste konnte nicht geladen werden – PDF-Klick-Mapping ist trotzdem möglich.',
                    err,
                );
            },
        });
    }

    private ensurePlacePlaceholder() {
        const all = this.allPlatzhalter();
        if (!this.placePlaceholder && all.length) {
            this.placePlaceholder = all[0].key;
        }
    }

    allPlatzhalter(): Platzhalter[] {
        return [
            ...this.platzhalter,
            ...this.customFields.map(c => ({
                key: c.key,
                label: (c.type === 'checkbox' ? 'Checkbox – ' : 'Freitext – ') + c.label,
            })),
        ];
    }

    addCustomField() {
        const label = (this.newCustomLabel || '').trim();
        if (!label) {
            this.messageService.infoMessage('Bitte Bezeichnung eingeben (z. B. „Bestandsproblem nein“).');
            return;
        }
        const slug =
            label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_|_$/g, '')
                .slice(0, 40) || 'feld';
        const key = 'custom.' + slug + '_' + Date.now().toString(36).slice(-4);
        const type = this.newCustomType === 'checkbox' ? 'checkbox' : 'text';
        this.customFields.push({ key, label, type });
        this.newCustomLabel = '';
        this.placePlaceholder = key;
        this.messageService.infoMessage(
            (type === 'checkbox' ? 'Checkbox' : 'Freitext') +
                ' „' +
                label +
                '“ angelegt – jetzt auf die PDF klicken.',
        );
    }

    removeCustomField(key: string) {
        this.customFields = this.customFields.filter(c => c.key !== key);
        this.entries.forEach(e => {
            if (e.placeholder === key) {
                e.placeholder = '';
            }
        });
        if (this.placePlaceholder === key) {
            this.placePlaceholder = this.allPlatzhalter()[0]?.key || '';
        }
    }

    isCheckboxKey(key: string): boolean {
        if (!key) {
            return false;
        }
        if (key.startsWith('befund_an.') || key.startsWith('rechnung_an.')) {
            return true;
        }
        return this.customFields.some(c => c.key === key && c.type === 'checkbox');
    }

    isSignatureKey(key: string): boolean {
        return key === 'ua.unterschrift';
    }

    private buildFeldMappingPayload() {
        const cleaned = this.entries
            .map(e => {
                if (e.kind === 'overlay') {
                    return {
                        kind: 'overlay',
                        page: Number(e.page) || 1,
                        x: Number(e.x) || 0,
                        y: Number(e.y) || 0,
                        size: Number(e.size) || (this.isCheckboxKey(e.placeholder) ? 11 : 9),
                        width: Number(e.width) > 20 ? Number(e.width) : undefined,
                        height: Number(e.height) > 8 ? Number(e.height) : undefined,
                        lines: Number(e.lines) > 1 ? Number(e.lines) : undefined,
                        placeholder: (e.placeholder || '').trim(),
                    };
                }
                return {
                    kind: 'field',
                    name: (e.name || '').trim(),
                    placeholder: (e.placeholder || '').trim(),
                };
            })
            .filter(e => {
                if (!e.placeholder) {
                    return false;
                }
                if (e.kind === 'field') {
                    return !!(e as any).name;
                }
                return true;
            });
        return {
            entries: cleaned,
            custom_fields: this.customFields
                .map(c => ({
                    key: c.key,
                    label: (c.label || '').trim(),
                    type: c.type === 'checkbox' ? 'checkbox' : 'text',
                }))
                .filter(c => c.key && c.label),
        };
    }

    previewBeispiel() {
        if (!this.pdfSrc) {
            this.messageService.infoMessage('PDF ist noch nicht geladen.');
            return;
        }
        this.previewing = true;
        const input = new PdfViewerDialogInput(
            'lieferant_dokument/preview_fill.php',
            new HttpParams(),
            'Beispielvorschau – ' + (this.data.titel || 'Formular'),
        );
        input.object = {
            ld_id: this.data.ld_id,
            feld_mapping: this.buildFeldMappingPayload(),
        };
        this.pdfViewer.openPdfViewer(input).subscribe({
            next: () => {
                this.previewing = false;
            },
            error: () => {
                this.previewing = false;
            },
        });
        // Dialog öffnet async; Flag nach kurzer Zeit zurücksetzen
        setTimeout(() => {
            this.previewing = false;
        }, 1500);
    }

    private ensureFallbackPlatzhalter() {
        if (!this.platzhalter.length) {
            this.platzhalter = [
                { key: 'praxis.name', label: 'Praxis – Name' },
                { key: 'praxis.strasse', label: 'Praxis – Straße' },
                { key: 'praxis.plz_ort', label: 'Praxis – PLZ Ort' },
                { key: 'praxis.telefon', label: 'Praxis – Telefon' },
                { key: 'praxis.email', label: 'Praxis – E-Mail' },
                { key: 'kunde.name', label: 'Tierbesitzer – Name' },
                { key: 'kunde.strasse', label: 'Tierbesitzer – Straße' },
                { key: 'kunde.plz_ort', label: 'Tierbesitzer – PLZ Ort' },
                { key: 'kunde.telefon', label: 'Tierbesitzer – Telefon' },
                { key: 'standort.name', label: 'Standort – Name' },
                { key: 'standort.strasse', label: 'Standort – Straße' },
                { key: 'standort.plz_ort', label: 'Standort – PLZ Ort' },
                { key: 'standort.vvvo', label: 'Standort – VVVO' },
                { key: 'ua.unterschrift', label: 'Unterschrift (hinterlegt)' },
            ];
        }
        this.ensurePlacePlaceholder();
    }

    private loadPdf(params: HttpParams) {
        this.pdfLoading = true;
        this.pdfError = '';
        this.pdfSrc = null;
        this.http
            .get(this.apiBaseUrl + 'lieferant_dokument/read_pdf.php', {
                params,
                responseType: 'blob',
            })
            .subscribe({
                next: response => {
                    if (!response || response.size <= 0) {
                        this.pdfLoading = false;
                        this.pdfError = 'PDF leer.';
                        return;
                    }
                    // Fehler-JSON als Blob?
                    if (response.type && response.type.indexOf('json') >= 0) {
                        response.text().then(t => {
                            this.pdfLoading = false;
                            this.pdfError = this.parseErrorText(t);
                        });
                        return;
                    }
                    response
                        .arrayBuffer()
                        .then(buf => {
                            const bytes = new Uint8Array(buf);
                            if (!this.looksLikePdf(bytes)) {
                                // Oft application/octet-stream mit JSON-Fehlertext
                                const text = new TextDecoder().decode(bytes.slice(0, 2000));
                                this.pdfLoading = false;
                                this.pdfError = this.parseErrorText(text) || 'Antwort ist kein PDF.';
                                return;
                            }
                            this.pdfSrc = bytes;
                            this.pdfLoading = false;
                            this.pdfError = '';
                        })
                        .catch(() => {
                            this.pdfLoading = false;
                            this.pdfError = 'PDF konnte nicht gelesen werden.';
                        });
                },
                error: err => {
                    this.pdfLoading = false;
                    this.pdfError = 'PDF konnte nicht geladen werden (Rechte/Netzwerk).';
                    this.messageService.errorMessage(this.pdfError, err);
                },
            });
    }

    private looksLikePdf(bytes: Uint8Array): boolean {
        // %PDF
        return (
            bytes.length >= 4 &&
            bytes[0] === 0x25 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x44 &&
            bytes[3] === 0x46
        );
    }

    private parseErrorText(t: string): string {
        let msg = 'PDF konnte nicht geladen werden.';
        try {
            const j = JSON.parse(t);
            msg = j.errortext || j.fehler || j.message || msg;
        } catch {
            if (t && t.trim().length && t.trim().length < 300) {
                msg = t.trim();
            }
        }
        return msg;
    }

    onPdfLoad(pdf: {
        numPages?: number;
        getPage?: (n: number) => Promise<{ getViewport: (o: { scale: number }) => { width: number; height: number } }>;
    }) {
        if (pdf?.numPages && pdf.numPages > 0) {
            this.pageCount = Math.max(this.pageCount, pdf.numPages);
        }
        // Seitenmaße aus dem echten PDF (wie pdfmerger Mediabox) – nicht 595×842-Fallback
        if (pdf?.getPage && pdf.numPages) {
            const n = pdf.numPages;
            Promise.all(
                Array.from({ length: n }, (_, i) =>
                    pdf.getPage!(i + 1).then(page => {
                        const vp = page.getViewport({ scale: 1 });
                        return { page: i + 1, width: vp.width, height: vp.height };
                    }),
                ),
            ).then(pages => {
                this.pages = pages;
                this.refreshMarkerLayout();
            });
        }
        setTimeout(() => this.bindViewerScroll(), 0);
        setTimeout(() => this.bindViewerScroll(), 150);
        setTimeout(() => this.bindViewerScroll(), 400);
    }

    onPdfError(err: unknown) {
        console.error('PDF-Viewer Fehler', err);
        this.pdfError = 'PDF-Viewer konnte die Datei nicht anzeigen.';
        this.pdfSrc = null;
        this.unbindViewerScroll();
    }

    ngOnDestroy(): void {
        this.stopDrag();
        this.unbindViewerScroll();
        this.pdfSrc = null;
    }

    private bindViewerScroll() {
        const host = this.pdfHost?.nativeElement;
        if (!host) {
            return;
        }
        const el = host.querySelector('.ng2-pdf-viewer-container') as HTMLElement | null;
        if (el && el !== this.scrollEl) {
            this.unbindViewerScroll();
            this.scrollEl = el;
            el.addEventListener('scroll', this.onViewerScroll, { passive: true });
            window.addEventListener('resize', this.onWindowResize, { passive: true });
        }
        this.refreshMarkerLayout();
    }

    private unbindViewerScroll() {
        if (this.scrollEl) {
            this.scrollEl.removeEventListener('scroll', this.onViewerScroll);
            this.scrollEl = null;
        }
        window.removeEventListener('resize', this.onWindowResize);
    }

    private refreshMarkerLayout() {
        this.layoutTick++;
        this.cdr.detectChanges();
    }

    /** Gerenderte PDF-Canvas (ohne Page-Padding). */
    private getPageElement(): HTMLElement | null {
        const host = this.pdfHost?.nativeElement;
        if (!host) {
            return null;
        }
        return (
            (host.querySelector('.page canvas') as HTMLElement) ||
            (host.querySelector('.canvasWrapper canvas') as HTMLElement) ||
            (host.querySelector('.page') as HTMLElement) ||
            null
        );
    }

    pageWidth(): number {
        const p = this.pages.find(x => x.page === this.currentPage);
        return p?.width || 595;
    }

    pageHeight(): number {
        const p = this.pages.find(x => x.page === this.currentPage);
        return p?.height || 842;
    }

    overlaysOnCurrentPage(): MappingEntry[] {
        return this.entries.filter(e => e.kind === 'overlay' && (e.page || 1) === this.currentPage);
    }

    /**
     * Marker relativ zur Canvas.
     * Text: Anker = links/Baseline; Checkbox: Anker = Mitte (fill align=center).
     */
    markerStyle(e: MappingEntry): { [k: string]: string } {
        void this.layoutTick;
        const host = this.pdfHost?.nativeElement;
        const pageEl = this.getPageElement();
        if (!host || !pageEl) {
            return { display: 'none' };
        }
        const hostRect = host.getBoundingClientRect();
        const pageRect = pageEl.getBoundingClientRect();
        if (pageRect.width <= 0 || pageRect.height <= 0) {
            return { display: 'none' };
        }
        const w = this.pageWidth();
        const h = this.pageHeight();
        const x = Number(e.x) || 0;
        const y = Number(e.y) || 0;
        const leftPx = pageRect.left - hostRect.left + (x / w) * pageRect.width;
        const topPx = pageRect.top - hostRect.top + ((h - y) / h) * pageRect.height;
        return {
            left: leftPx + 'px',
            top: topPx + 'px',
        };
    }

    /** Rechteck des Textblocks in Pixeln, an der Baseline ausgerichtet. */
    textBlockStyle(e: MappingEntry): { [k: string]: string } {
        void this.layoutTick;
        const pageEl = this.getPageElement();
        if (!pageEl) {
            return { display: 'none' };
        }
        const pageRect = pageEl.getBoundingClientRect();
        const w = this.pageWidth();
        const h = this.pageHeight();
        if (pageRect.width <= 0 || w <= 0) {
            return { display: 'none' };
        }
        const scale = pageRect.width / w;
        const size = Number(e.size) || 9;
        const lines = Number(e.lines) || 1;
        const boxH =
            Number(e.height) > 8 ? Number(e.height) * scale : lines * size * 1.25 * scale;
        return {
            width: Math.round((Number(e.width) || 0) * scale) + 'px',
            height: Math.round(boxH) + 'px',
        };
    }

    /** Client → PDF-Punkte (unten links), bezogen auf die Canvas. */
    private clientToPdf(clientX: number, clientY: number): { x: number; y: number } | null {
        const pageEl = this.getPageElement();
        if (!pageEl) {
            return null;
        }
        const rect = pageEl.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }
        const pdfW = this.pageWidth();
        const pdfH = this.pageHeight();
        const relX = (clientX - rect.left) / rect.width;
        const relY = (clientY - rect.top) / rect.height;
        if (relX < -0.02 || relX > 1.02 || relY < -0.02 || relY > 1.02) {
            return null;
        }
        const clampedX = Math.min(1, Math.max(0, relX));
        const clampedY = Math.min(1, Math.max(0, relY));
        return {
            x: Math.round(clampedX * pdfW * 10) / 10,
            y: Math.round((1 - clampedY) * pdfH * 10) / 10,
        };
    }

    startPlaceMode() {
        if (!this.placePlaceholder) {
            this.messageService.infoMessage('Bitte zuerst einen VetVet-Platzhalter wählen.');
            return;
        }
        if (!this.pdfSrc) {
            this.messageService.infoMessage('PDF ist noch nicht geladen.');
            return;
        }
        this.stopDrag();
        this.placeMode = true;
        const key = this.placePlaceholder;
        this.messageService.infoMessage(
            this.isCheckboxKey(key)
                ? 'Jetzt in die Mitte des Kästchens klicken.'
                : this.isSignatureKey(key)
                  ? 'Kasten für die Unterschrift aufziehen. Eingesetzt wird die hinterlegte Unterschrift.'
                  : 'Links an der Zeile ansetzen und nach rechts ziehen. So lang wird diese eine Zeile.',
        );
    }

    cancelPlaceMode() {
        this.placeMode = false;
        this.placeStart = null;
        this.placeDraft = null;
    }

    onPlaceMouseDown(ev: MouseEvent) {
        if (!this.placeMode) {
            return;
        }
        const pos = this.clientToPdf(ev.clientX, ev.clientY);
        if (!pos) {
            return;
        }
        ev.preventDefault();
        if (this.isCheckboxKey(this.placePlaceholder)) {
            this.commitPlace(pos.x, pos.y, 0);
            return;
        }
        this.placeStart = pos;
        this.placeDraft = { x: pos.x, y: pos.y, width: 0, height: 0 };
    }

    private commitPlace(x: number, y: number, width: number, height = 0) {
        const checkbox = this.isCheckboxKey(this.placePlaceholder);
        const signature = this.isSignatureKey(this.placePlaceholder);
        this.entries.push({
            kind: 'overlay',
            page: this.currentPage,
            x,
            y,
            size: checkbox ? 11 : 9,
            width: !checkbox && width > 12 ? Math.round(width * 10) / 10 : undefined,
            height: signature && height > 8 ? Math.round(height * 10) / 10 : undefined,
            lines: !checkbox && !signature && width > 12 ? 1 : undefined,
            placeholder: this.placePlaceholder,
        });
        this.placeMode = false;
        this.placeStart = null;
        this.placeDraft = null;
        this.messageService.infoMessage(
            this.platzhalterLabel(this.placePlaceholder) +
                (checkbox
                    ? ' (X/nix) gesetzt.'
                    : signature
                      ? ' gesetzt. Beim Abschließen wird die hinterlegte Unterschrift dort eingesetzt.'
                      : ' Zeile gesetzt – gleiche Zeile nochmal aufziehen für die nächste Reihe.'),
        );
    }

    draftStyle(): { [k: string]: string } {
        if (!this.placeDraft) {
            return { display: 'none' };
        }
        const fake: MappingEntry = {
            kind: 'overlay',
            placeholder: '',
            x: this.placeDraft.x,
            y: this.placeDraft.y,
            size: 9,
            width: Math.max(this.placeDraft.width, 8),
            height: this.placeDraft.height > 8 ? this.placeDraft.height : undefined,
            lines: 1,
        };
        const pos = this.markerStyle(fake);
        const box = this.textBlockStyle(fake);
        const signature = this.isSignatureKey(this.placePlaceholder);
        return { ...pos, ...box, transform: signature ? 'none' : 'translateY(-0.8em)' };
    }

    onMarkerMouseDown(ev: MouseEvent, entry: MappingEntry) {
        if (this.placeMode || entry.kind !== 'overlay') {
            return;
        }
        ev.preventDefault();
        ev.stopPropagation();
        this.dragEntry = entry;
        this.dragging = true;
    }

    onWidthResizeDown(ev: MouseEvent, entry: MappingEntry) {
        ev.preventDefault();
        ev.stopPropagation();
        this.resizeWidthEntry = entry;
    }

    @HostListener('document:mousemove', ['$event'])
    onDocumentMouseMove(ev: MouseEvent) {
        const pos = this.clientToPdf(ev.clientX, ev.clientY);
        if (this.placeStart && pos) {
            this.placeDraft = {
                x: this.placeStart.x,
                y: this.placeStart.y,
                width: Math.max(0, pos.x - this.placeStart.x),
                height: this.isSignatureKey(this.placePlaceholder)
                    ? Math.max(0, this.placeStart.y - pos.y)
                    : 0,
            };
            return;
        }
        if (this.resizeWidthEntry && pos) {
            this.resizeWidthEntry.width = Math.max(20, Math.round((pos.x - (this.resizeWidthEntry.x || 0)) * 10) / 10);
            this.resizeWidthEntry.lines = 1;
            return;
        }
        if (!this.dragEntry || !pos) {
            return;
        }
        this.dragEntry.x = pos.x;
        this.dragEntry.y = pos.y;
    }

    @HostListener('document:mouseup')
    onDocumentMouseUp() {
        if (this.placeStart) {
            const width = this.placeDraft?.width || 0;
            this.commitPlace(this.placeStart.x, this.placeStart.y, width, this.placeDraft?.height || 0);
        }
        this.resizeWidthEntry = null;
        this.stopDrag();
    }

    private stopDrag() {
        this.dragEntry = null;
        this.dragging = false;
    }

    platzhalterLabel(key: string): string {
        const custom = this.customFields.find(c => c.key === key);
        if (custom) {
            return (custom.type === 'checkbox' ? 'Checkbox – ' : 'Freitext – ') + custom.label;
        }
        return this.platzhalter.find(p => p.key === key)?.label || key;
    }

    prevPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            setTimeout(() => this.bindViewerScroll(), 50);
        }
    }

    nextPage() {
        if (this.currentPage < this.pageCount) {
            this.currentPage++;
            setTimeout(() => this.bindViewerScroll(), 50);
        }
    }

    addFieldRow() {
        this.entries.push({ kind: 'field', name: '', placeholder: '' });
    }

    importDetectedFields() {
        const existing = new Set(this.entries.filter(e => e.kind === 'field' && e.name).map(e => e.name));
        for (const f of this.detectedFields) {
            if (!existing.has(f.name)) {
                this.entries.push({ kind: 'field', name: f.name, placeholder: '' });
            }
        }
    }

    removeEntry(i: number) {
        this.entries.splice(i, 1);
    }

    cancel() {
        this.dialogRef.close();
    }

    save() {
        const payload = this.buildFeldMappingPayload();

        this.saving = true;
        this.api
            .post(this.apiBaseUrl + 'lieferant_dokument/update_feld_mapping.php', {
                ld_id: this.data.ld_id,
                feld_mapping: payload,
            })
            .subscribe({
                next: () => {
                    this.saving = false;
                    this.messageService.infoMessage('Feld-Mapping gespeichert.');
                    this.dialogRef.close(true);
                },
                error: err => {
                    this.saving = false;
                    this.messageService.errorMessage('Speichern fehlgeschlagen!', err);
                },
            });
    }
}
